import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../../router/all_routes";
import { useT } from "../../../../core/i18n";
import Api from "../../../../core/data/redux/utils/apiCall";
import {
  SWF_STATUS_FILTER_OPTIONS,
  findAllSwf,
  getLoggedInEmpId,
  getStatusBadgeClass,
  getStatusLabel,
  mapApiItemToCard,
  syncSwf,
} from "./staffWelfare.service";

const StaffWelfareList: React.FC = () => {
  const { t } = useT();
  const navigate = useNavigate();
  const routes = all_routes;

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [status, setStatus] = useState("");
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showChecklist, setShowChecklist] = useState(false);

  const loadList = useCallback(
    async (overrides?: { status?: string; fromDate?: string; toDate?: string }) => {
      const statusFilter = overrides?.status ?? status;
      const from = overrides?.fromDate ?? fromDate;
      const to = overrides?.toDate ?? toDate;
      setLoading(true);
      try {
        const list = await findAllSwf({
          page: 1,
          limit: 100,
          status: statusFilter,
          startDate: from,
          endDate: to,
        });
        setItems(list.map((item: any) => mapApiItemToCard(item)));
      } catch (err: any) {
        setItems([]);
        toast.error(Api.getErrorMessage(err) || t("failed_to_load_details"));
      } finally {
        setLoading(false);
      }
    },
    [status, fromDate, toDate, t]
  );

  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSync = async () => {
    const empId = getLoggedInEmpId();
    if (!empId) {
      toast.error(t("employee_id_not_found"));
      return;
    }
    setSyncing(true);
    try {
      const res: any = await syncSwf(empId);
      toast.success(res?.message || t("synced_successfully") || "Sync successful");
    } catch (err: any) {
      toast.error(Api.getErrorMessage(err) || "Sync failed");
    } finally {
      setSyncing(false);
      await loadList();
    }
  };

  const L = (key: string, fallback: string) => {
    const v = t(key);
    return v === key ? fallback : v;
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">{t("staff_welfare_fund_grants")}</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.services.list}>Services</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  {t("staff_welfare_fund_grants")}
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={handleSync}
              disabled={syncing || loading}
            >
              <i className={`ti ti-refresh me-1 ${syncing ? "spin" : ""}`}></i>
              {syncing ? t("syncing") : t("sync")}
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => setShowChecklist(true)}
            >
              <i className="ti ti-plus me-1"></i>
              {t("create_new_request")}
            </button>
          </div>
        </div>

        <div className="card border-0 shadow-sm mb-3">
          <div className="card-body">
            <div className="row g-3 align-items-end">
              <div className="col-md-3">
                <label className="form-label fw-semibold small">{t("start_date")}</label>
                <input
                  type="date"
                  className="form-control"
                  value={fromDate}
                  max={toDate || new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label fw-semibold small">{t("end_date")}</label>
                <input
                  type="date"
                  className="form-control"
                  value={toDate}
                  min={fromDate || undefined}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (fromDate && value < fromDate) {
                      toast.error("End date cannot be before start date");
                      return;
                    }
                    setToDate(value);
                  }}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label fw-semibold small">{t("filter_by_status")}</label>
                <select
                  className="form-select"
                  value={status}
                  onChange={(e) => {
                    const value = e.target.value;
                    setStatus(value);
                    loadList({ status: value });
                  }}
                >
                  {SWF_STATUS_FILTER_OPTIONS.map((opt) => {
                    const label = t(opt.labelKey) === opt.labelKey ? opt.label : t(opt.labelKey);
                    return (
                      <option key={opt.key || "all"} value={opt.key}>
                        {label}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div className="col-md-3 d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-danger flex-fill"
                  onClick={() => loadList()}
                  disabled={loading}
                >
                  {t("refresh_list")}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    setFromDate("");
                    setToDate("");
                    setStatus("");
                    loadList({ status: "", fromDate: "", toDate: "" });
                  }}
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-danger" role="status" />
          </div>
        ) : items.length === 0 ? (
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center py-5 text-muted">
              {t("no_request_available") || t("no_records_found")}
            </div>
          </div>
        ) : (
          <div className="row g-3">
            {items.map((item) => {
              const refNo = item.referenceNumber || "-";
              return (
                <div className="col-xl-6" key={item.id || refNo}>
                  <div className="card border shadow-sm h-100">
                    <div className="card-header bg-white d-flex justify-content-between align-items-center">
                      <span className="fw-bold">
                        {t("reference_no")}: {refNo}
                      </span>
                      <span className={getStatusBadgeClass(item.requestStatus)}>
                        {getStatusLabel(item.requestStatus, t)}
                      </span>
                    </div>
                    <div className="card-body">
                      <InfoRow label={t("SWF_Claim_Type")} value={item.claimType || "-"} />
                      <InfoRow
                        label={t("claim_amount")}
                        value={item.claimAmount || "N/A"}
                      />
                      <InfoRow
                        label={t("creation_date")}
                        value={item.requestCreationDate || "-"}
                      />
                      <InfoRow
                        label={L("Last_Latest_Approved", "Last/Latest Approved")}
                        value={item.approverUserID || "N/A"}
                      />
                      <InfoRow label={t("Remark")} value={item.hRemarks || "N/A"} />
                      <div className="d-flex gap-2 mt-3">
                        <button
                          type="button"
                          className="btn btn-danger flex-fill"
                          onClick={() => {
                            const path = routes.staffWelfareFund.view.replace(
                              ":id",
                              encodeURIComponent(item.id || refNo)
                            );
                            navigate(
                              `${path}?purpose=${encodeURIComponent(item.purpose || "")}`
                            );
                          }}
                        >
                          {t("view_details")}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {showChecklist && (
          <div className="modal fade show d-block" tabIndex={-1} style={{ background: "rgba(0,0,0,0.45)" }}>
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {t("Checklist_Application_for_Staff_Welfare_Fund_Grant")}
                  </h5>
                  <button type="button" className="btn-close" onClick={() => setShowChecklist(false)} />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <h6 className="fw-bold mb-1">
                      {t("Payment_Information")} / {t("Payment_Information_Hindi")}
                    </h6>
                    <p className="mb-1">{t("Payment_off_cycle")}</p>
                    <p className="text-muted mb-0">{t("Payment_off_cycle_hindi")}</p>
                  </div>
                  <div className="mb-3">
                    <h6 className="fw-bold mb-1">
                      {t("Excellence_in_Sports")} / {t("Excellence_in_Sports_Hindi")}
                    </h6>
                    <p className="mb-1">{t("Application_form_hard_copy")}</p>
                    <p className="text-muted mb-0">{t("Application_form_hard_copy_hindi")}</p>
                  </div>
                  <div>
                    <h6 className="fw-bold mb-1">
                      {t("Contact_for_Queries")} / {t("Contact_for_Queries_Hindi")}
                    </h6>
                    <p className="mb-1">{t("Email_for_queries")}</p>
                    <p className="text-muted mb-0">{t("Email_for_queries_hindi")}</p>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-light" onClick={() => setShowChecklist(false)}>
                    {t("cancel")}
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => {
                      setShowChecklist(false);
                      navigate(routes.staffWelfareFund.create);
                    }}
                  >
                    {t("Proceed_to_Staff_Welfare_application_form")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <style>{`
        .spin { animation: swf-spin 1s linear infinite; }
        @keyframes swf-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

const InfoRow: React.FC<{ label: string; value: any }> = ({ label, value }) => (
  <div className="d-flex justify-content-between gap-3 mb-2">
    <span className="text-muted fw-semibold">{label}</span>
    <span className="text-dark text-end">{value ?? "-"}</span>
  </div>
);

export default StaffWelfareList;
